import { useMemo } from 'react'
import { Check, Wallet } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { ChainIcon } from '@/components/ChainIcon'
import { useThemeColors } from '@/hooks/useThemeColors'
import { CHAIN_REGISTRY, BRIDGE_CHAINS } from '@/config/chains'
import { TOKEN_PICKER_LIST } from '@/config/tokens'
import { FONT_BODY } from '@/config/theme'
import type { Chain, ChainBalance, ChainBalanceMap } from '@/types'

interface ChainPickerModalProps {
  show: boolean
  onClose: () => void
  selectedChain: Chain | null
  onSelect: (chain: Chain) => void
  disabledChain?: Chain | null
  disabledLabel?: string
  balances?: ChainBalanceMap
  isLoading?: boolean
  evmConnected?: boolean
  solanaConnected?: boolean
  tronConnected?: boolean
  minAmountByDest?: Record<string, number>
  minAmountFlat?: number
  onConnectWallet?: (family: 'evm' | 'solana' | 'tron') => void
  chains?: Chain[]
  selectedToken?: string
  /** DESTINATION picker: allow selecting a chain WITHOUT a connected wallet of that family. You can
   *  receive to a pasted address, so a dest-chain pick must never be hijacked into a wallet-connect
   *  (that left destinationChain on the previous chain and mis-validated pasted addresses). Source
   *  picker leaves this false — you DO need a wallet to send FROM a chain. */
  allowUnconnectedSelect?: boolean
}

function formatBalance(value: string, isNative = false): string {
  const num = parseFloat(value)
  if (isNaN(num)) return '0.00'
  if (isNative) {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: num < 0.01 ? 6 : 4,
      maximumFractionDigits: num < 0.01 ? 6 : 4,
    })
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: num < 1 ? 4 : 2,
    maximumFractionDigits: num < 1 ? 4 : 2,
  })
}

function resolveTokenDisplay(
  selectedToken: string,
  chain: Chain,
  balance: ChainBalance | undefined,
): { value: string | null; symbol: string; isNative: boolean } {
  const tokenDef = TOKEN_PICKER_LIST.find(t => t.symbol === selectedToken)

  if (tokenDef?.isNative) {
    // Only show native balance on chains where this token exists
    if (tokenDef.availableOn && !tokenDef.availableOn.includes(chain)) {
      return { value: null, symbol: selectedToken, isNative: true }
    }
    return { value: balance?.native ?? null, symbol: selectedToken, isNative: true }
  }

  if (selectedToken === 'USDT') {
    return { value: balance?.usdt ?? null, symbol: 'USDT', isNative: false }
  }

  // #174 — Robinhood's dollar (and the eth/sol source asset) is USDG, not USDC.
  if (selectedToken === 'USDG') {
    return { value: balance?.usdg ?? null, symbol: 'USDG', isNative: false }
  }

  return { value: balance?.usdc ?? null, symbol: 'USDC', isNative: false }
}

function sortChains(
  chains: Chain[],
  balances: ChainBalanceMap,
  evmConnected: boolean,
  solanaConnected: boolean,
  tronConnected: boolean,
  disabledChain: Chain | null,
  selectedToken: string
): Chain[] {
  const isConnected = (chain: Chain): boolean => {
    const meta = CHAIN_REGISTRY[chain]
    if (meta.addrType === 'solana') return solanaConnected
    if (meta.addrType === 'tron') return tronConnected
    return evmConnected
  }

  const getBalance = (chain: Chain): number => {
    const bal = balances[chain]
    const tokenDef = TOKEN_PICKER_LIST.find(t => t.symbol === selectedToken)
    if (tokenDef?.isNative) {
      if (tokenDef.availableOn && !tokenDef.availableOn.includes(chain)) return 0
      return bal?.native ? parseFloat(bal.native) : 0
    }
    if (selectedToken === 'USDT') return bal?.usdt ? parseFloat(bal.usdt) : 0
    if (selectedToken === 'USDG') return bal?.usdg ? parseFloat(bal.usdg) : 0 // #174 — RH/USDG sort key
    return bal?.usdc ? parseFloat(bal.usdc) : 0
  }

  return [...chains].sort((a, b) => {
    // Disabled chains always last
    if (a === disabledChain) return 1
    if (b === disabledChain) return -1

    const aConn = isConnected(a)
    const bConn = isConnected(b)

    // Connected before disconnected
    if (aConn && !bConn) return -1
    if (!aConn && bConn) return 1

    if (aConn && bConn) {
      const aBal = getBalance(a)
      const bBal = getBalance(b)
      // Both have balance > 0: descending
      if (aBal > 0 && bBal > 0) return bBal - aBal
      // Balance > 0 before 0
      if (aBal > 0) return -1
      if (bBal > 0) return 1
      // Both 0: alphabetical
      return CHAIN_REGISTRY[a].name.localeCompare(CHAIN_REGISTRY[b].name)
    }

    // Both disconnected: alphabetical
    return CHAIN_REGISTRY[a].name.localeCompare(CHAIN_REGISTRY[b].name)
  })
}

export function ChainPickerModal({
  show,
  onClose,
  selectedChain,
  onSelect,
  disabledChain,
  disabledLabel = 'Same as source',
  balances = {},
  isLoading = false,
  evmConnected = false,
  solanaConnected = false,
  tronConnected = false,
  minAmountFlat,
  onConnectWallet,
  chains: chainsProp,
  selectedToken = 'USDC',
  allowUnconnectedSelect = false,
}: ChainPickerModalProps) {
  const c = useThemeColors()
  const sortedChains = useMemo(
    () => sortChains(chainsProp ?? BRIDGE_CHAINS, balances, evmConnected, solanaConnected, tronConnected, disabledChain ?? null, selectedToken),
    [chainsProp, balances, evmConnected, solanaConnected, tronConnected, disabledChain, selectedToken]
  )

  // Below-min hint value (#27/#60). minAmountFlat is the SAME single-source min the swap-card renders as
  // "Minimum ~X <token>", so the two surfaces show an identical figure. #60: show 2 decimals (2 → "~2.00",
  // 1.1 → "~1.10") — the tilde signals a cent-level ballpark, so the 2nd decimal must be visible. The TOKEN
  // UNIT is appended per-row (resolved.symbol) so the min reads in token terms (e.g. "~1.10 USDC") rather
  // than a fiat "$", which would clash with the app's currency picker. #60: the below-min branch fires for
  // both USDC and USDT (both ≈ $1), so resolved.symbol carries the correct unit per row; native is excluded.
  const minDisplay = minAmountFlat != null && minAmountFlat > 0
    ? `~${minAmountFlat.toFixed(2)}`
    : ''

  return (
    <Modal show={show} onClose={onClose} title="Select network">
      <div>
        {sortedChains.map((chain) => {
          const meta = CHAIN_REGISTRY[chain]
          const isSelected = chain === selectedChain
          const isDisabled = chain === disabledChain
          const connected = meta.addrType === 'solana' ? solanaConnected : meta.addrType === 'tron' ? tronConnected : evmConnected
          const balance = balances[chain]
          const resolved = resolveTokenDisplay(selectedToken, chain, balance)
          const balanceNum = resolved.value ? parseFloat(resolved.value) : 0

          // belowMinimum = stablecoin balance on this chain is between 0 and the minimum. This is a SOURCE-
          // picker concept ONLY: you can't SEND below the minimum. The DEST (receive) picker passes no
          // minAmountFlat, so it never balance-gates — your balance on the destination is irrelevant when
          // receiving. (This previously gated the dest picker on minAmountByDest, making dest chains where
          // you held a small balance unclickable.)
          // #60: applies to BOTH USDC and USDT (both ≈ $1, so the USD `minAmountFlat` compares directly to
          // the token balance). NATIVE is still skipped — its balance is in native units, not USD, so it
          // can't be compared to the USD min without a price (deferred).
          let belowMinimum = false
          if (minAmountFlat !== undefined && connected && balance && !resolved.isNative) {
            belowMinimum = balanceNum > 0 && balanceNum < minAmountFlat
          }

          // Color-to-select (#27): a below-minimum chain stays SELECTABLE so the user can pick it to
          // explore the route — the min is surfaced as a warning-coloured hint instead of hard-disabling
          // the row. The genuine below-min block lives at SUBMIT (the swap-card CTA gates on the same
          // single-source min), so allowing selection here can't push an under-min tx through. Only the
          // same-as (disabledChain) row is truly unclickable.
          const isClickable = !isDisabled

          return (
            <button
              key={chain}
              onClick={() => {
                if (isDisabled) return
                // Source picker: an unconnected chain routes to wallet-connect (you must have a wallet to
                // send FROM it). Destination picker (allowUnconnectedSelect): ALWAYS select — you can
                // receive to a pasted address, so a dest pick must set the chain, never hijack to connect
                // (which left destinationChain stale on the old chain and mis-validated pasted addresses).
                if (!connected && !allowUnconnectedSelect) {
                  onConnectWallet?.(meta.addrType === 'tron' ? 'tron' : meta.addrType === 'solana' ? 'solana' : 'evm')
                  return
                }
                onSelect(chain)
                onClose()
              }}
              disabled={isDisabled}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: '14px 20px',
                background: 'transparent', border: 'none',
                borderBottom: `1px solid ${c.dv}`,
                cursor: isClickable ? 'pointer' : 'default',
                opacity: isDisabled ? 0.4 : 1,
                transition: 'background 0.15s',
                fontFamily: FONT_BODY,
              }}
              onMouseEnter={(e) => {
                if (isClickable) (e.currentTarget as HTMLButtonElement).style.background = c.blB
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent'
              }}
            >
              {/* Chain icon in tinted container */}
              <div style={{
                width: 36, height: 36, borderRadius: 12,
                background: meta.color + '18',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <ChainIcon chain={chain} size={24} />
              </div>

              {/* Chain name + balance/status */}
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 500, color: c.tx }}>
                  {meta.name}
                </div>

                {/* Status line: loading / disabled / disconnected / below-min / balance */}
                <div style={{ fontSize: 12, marginTop: 2 }}>
                  {isDisabled ? (
                    <span style={{ color: c.mu }}>{disabledLabel}</span>
                  ) : !connected ? (
                    <span style={{ color: c.ac, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Wallet size={11} />
                      Connect {meta.addrType === 'tron' ? 'Tron' : meta.addrType === 'solana' ? 'Solana' : 'EVM'} wallet
                    </span>
                  ) : isLoading ? (
                    <div style={{
                      height: 12, width: 64, borderRadius: 4,
                      background: c.dv, animation: 'shimmer 2s infinite',
                    }} />
                  ) : belowMinimum ? (
                    <span style={{ color: c.wC }}>
                      {formatBalance(String(balanceNum), resolved.isNative)} {resolved.symbol} — min {minDisplay} {resolved.symbol}
                    </span>
                  ) : resolved.value === null ? (
                    <span style={{ color: c.mu }}>— {resolved.symbol}</span>
                  ) : resolved.value ? (
                    <span style={{ color: c.mu }}>
                      {formatBalance(resolved.value, resolved.isNative)} {resolved.symbol}
                    </span>
                  ) : (
                    <span style={{ color: c.mu }}>0.00 {resolved.symbol}</span>
                  )}
                </div>
              </div>

              {/* Check icon on selected */}
              {isSelected && !isDisabled && (
                <Check size={17} color={c.gr} />
              )}
            </button>
          )
        })}

        {/* Empty state — no balance found for selected token */}
        {!isLoading && (evmConnected || solanaConnected || tronConnected) &&
          sortedChains.every(chain => {
            const resolved = resolveTokenDisplay(selectedToken, chain, balances[chain])
            return !resolved.value || parseFloat(resolved.value) === 0
          }) && (
          <div style={{
            padding: '16px 20px', fontSize: 14,
            color: c.mu, textAlign: 'center', fontFamily: FONT_BODY,
          }}>
            No {selectedToken} found.{selectedToken === 'USDC' && (import.meta.env.VITE_NETWORK || 'testnet') === 'testnet' && (
              <>{' '}Get testnet USDC at{' '}
                <a href="https://faucet.circle.com" target="_blank" rel="noopener noreferrer"
                   style={{ color: c.ac, textDecoration: 'none' }}>
                  faucet.circle.com
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
